import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

/**
 * Internal links with descriptive anchors.
 *
 * Fixes the audit's `t_orphan_pages` / `c_internal_links` findings properly:
 * every public page points at two to five genuinely related pages, using
 * anchors that say where they go ("Browse peer recovery sponsors", never
 * "click here"). Links are real <a href> elements rendered by react-router, so
 * a crawler sees an href even before JavaScript takes over, and the same list
 * is mirrored into the prerendered HTML by scripts/prerender-seo.js.
 *
 * Keep the list short and relevant - padding it to please an audit is exactly
 * what the audit warns against.
 */
export default function RelatedLinks({ links, heading = "Keep reading", testId = "related-links" }) {
  if (!links || !links.length) return null;
  return (
    <nav
      aria-label="Related pages"
      data-testid={testId}
      className="max-w-7xl mx-auto px-4 py-10"
    >
      <h2 className="font-display text-xl font-bold text-[#1F2A2E]">{heading}</h2>
      <ul className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {links.map((l) => (
          <li key={l.to}>
            <Link
              to={l.to}
              data-testid={`related-link-${l.to.replace(/\//g, "-").replace(/^-/, "")}`}
              className="group flex items-start justify-between gap-3 rounded-2xl bg-white border border-[#E3DED4] px-4 py-3.5 hover:border-[#1F6F78] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1F6F78] transition-colors"
            >
              <span>
                <span className="block font-semibold text-[#1F2A2E]">{l.label}</span>
                {l.hint ? (
                  <span className="block text-sm text-[#4B5A60] mt-0.5">{l.hint}</span>
                ) : null}
              </span>
              <ArrowRight
                className="w-4 h-4 mt-1 shrink-0 text-[#1F6F78] opacity-60 group-hover:opacity-100"
                aria-hidden="true"
              />
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  );
}
